import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import { Button, Form, Table } from "react-bootstrap";
import { formatDate } from "../../utils/helpers";
import CachedIcon from "mdi-react/CachedIcon";
import CreatableSelect from "react-select/creatable";
import { useState } from "react";
import FilledFormSelectorModal from "../CustomersEmail/FilledFormSelectorModal";
import FormPreviewModal from "../Requisition/FormPreviewModal";
import { TrashIcon } from "../Icons";

export default function OperatorCertification({
  Bar_Code,
  users = [],
  formik,
}) {
  const [showchildForm, setShowChildForm] = useState({
    show: false,
    certIndex: null,
    certificationId: null,
  });

  const [showFormPreviewModal, setShowFormPreviewModal] = useState({
    show: false,
    selectedFilledForm: null,
  });

  const { backendUrl, token } = useAuth();

  const normalizeArrayPayload = payload => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.data)) {
        return payload.data;
      }

      if (payload.data && Array.isArray(payload.data.assetRequirements)) {
        return payload.data.assetRequirements;
      }

      if (payload.data && Array.isArray(payload.data.rows)) {
        return payload.data.rows;
      }

      if (Array.isArray(payload.assetRequirements)) {
        return payload.assetRequirements;
      }

      if (Array.isArray(payload.rows)) {
        return payload.rows;
      }
    }

    return [];
  };

  const fetchCertificationTypes = async () => {
    const response = await fetch(
      `${backendUrl}/api/partnership/certification-types`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch certification types.");
    }

    const payload = await response.json();
    const rows = normalizeArrayPayload(payload);

    return rows.map(item => ({
      value:
        item.certificationId || item.id || item.value || item.code || item.name,
      label:
        item.certificationName ||
        item.name ||
        item.label ||
        item.certificationId ||
        item.id,
      // FIX 5: Preserve formId so defaultSelectedFormTemplate can use it
      formId: item.formId || item.form_id || null,
    }));
  };

  const fetchCertifications = async () => {
    const response = await fetch(
      `${backendUrl}/api/asset-requirements?Bar_Code=${Bar_Code}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch certifications.");
    }

    const payload = await response.json();
    return normalizeArrayPayload(payload);
  };

  const {
    data: certifications = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(
    // FIX 4: Include Bar_Code in the query key so it refetches when it changes
    ["asset-certifications", Bar_Code],
    fetchCertifications,
    {
      enabled: !!Bar_Code,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: certificationTypeOptions = [],
    isLoading: isLoadingCertificationTypes,
    error: certificationTypesError,
    refetch: refetchCertificationTypes,
  } = useQuery(["certification-types"], fetchCertificationTypes, {
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const getCertification = certificationId => {
    const selectedOption = certificationTypeOptions.find(
      option => String(option.value) === String(certificationId)
    );
    return selectedOption ? selectedOption : null;
  };

  return (
    <>
      <Form.Label>
        Operator:{" "}
        <Button
          variant=""
          onClick={() => {
            refetch();
            refetchCertificationTypes();
          }}
        >
          <CachedIcon />
        </Button>
      </Form.Label>

      <CreatableSelect
        isClearable
        onChange={selected => {
          formik.setFieldValue("operator", selected?.value ?? null);
          formik.setFieldValue("operatorStaff_ID", selected?.Staff_ID ?? null);
        }}
        options={users}
        value={{
          label: formik.values.operator || null,
          value: formik.values.operator || null,
          Staff_ID: formik.values.operatorStaff_ID || null,
        }}
      />

      <br />

      <Table
        striped
        borderless
        hover
        responsive
        className="product-table mb-0 border"
      >
        <thead>
          <tr>
            <th>Certification</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {certifications.length === 0 && (
            <tr>
              <td colSpan={3} className="text-center text-muted py-4">
                {isLoading || isFetching
                  ? "Loading certifications..."
                  : "No certifications found."}
              </td>
            </tr>
          )}

          {certifications.map((cert, index) => (
            <tr key={cert.id}>
              <td>
                <b
                  className="d-block"
                  title={getCertification(cert.certificationId)?.formId}
                >
                  {getCertification(cert.certificationId)?.label ?? "—"}
                </b>
                <i className="text-nowrap">
                  {cert.expiresAt ? (
                    formatDate(cert.expiresAt)
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </i>
              </td>

              {/* FIX 2: Use row's own `index` instead of showchildForm.certIndex */}
              <td>
                <div>
                  {formik?.values?.certification?.[index]?.filledForms?.map(
                    (item, formIndex) => (
                      <div
                        className="d-flex gap-2 text-nowrap mb-3"
                        key={formIndex}
                      >
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => {
                            setShowFormPreviewModal({
                              show: true,
                              selectedFilledForm: item,
                            });
                          }}
                        >
                          View: {item.formName} - {item.UserName}
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            const updatedForms = formik.values.certification[
                              index
                            ].filledForms.filter((_, i) => i !== formIndex);
                            formik.setFieldValue(
                              `certification.${index}.filledForms`,
                              updatedForms
                            );
                          }}
                        >
                          <TrashIcon />
                        </Button>
                      </div>
                    )
                  )}
                </div>

                <Button
                  size="sm"
                  variant="light-blue"
                  onClick={() =>
                    setShowChildForm({
                      show: true,
                      certIndex: index,
                      certificationId: cert.certificationId,
                    })
                  }
                >
                  Select Filled Form
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* FIX 1: Guard with showchildForm.show, not just showchildForm */}
      {showchildForm.show && (
        <FilledFormSelectorModal
          showchildForm={showchildForm.show}
          setShowChildForm={value =>
            setShowChildForm(prev => ({
              ...prev,
              show: value,
            }))
          }
          handleSelectedForms={forms => {
            const existing = formik.values.certification;

            // Force to array no matter what (null, undefined, {}, or [])
            const currentArray = Array.isArray(existing) ? [...existing] : [];

            currentArray[showchildForm.certIndex] = {
              ...(currentArray[showchildForm.certIndex] || {}),
              filledForms: forms.map(f => ({
                formName: f.formName,
                UserName: f.UserName,
                FormID: f.FormID,
                id: f.id,
              })),
            };

            formik.setFieldValue("certification", currentArray);
            setShowChildForm(prev => ({ ...prev, show: false }));
          }}
          defaultSelectedFormTemplate={{
            label:
              getCertification(showchildForm.certificationId)?.label ||
              "Select Form",
            value:
              getCertification(showchildForm.certificationId)?.formId || null,
          }}
        />
      )}

      {showFormPreviewModal.show && showFormPreviewModal.selectedFilledForm ? (
        <FormPreviewModal
          showFormPreviewModal={showFormPreviewModal.show}
          setShowFormPreviewModal={value =>
            setShowFormPreviewModal(prev => ({
              ...prev,
              show: value,
            }))
          }
          template={showFormPreviewModal.selectedFilledForm.filledFormData}
          selectedFilledForm={showFormPreviewModal.selectedFilledForm}
          setSelectedFilledForm={value =>
            setShowFormPreviewModal(prev => ({
              ...prev,
              selectedFilledForm: value,
            }))
          }
        />
      ) : null}
    </>
  );
}
